import React, {
    useMemo,
    useState,
} from "react";

import {
    FiCheck,
    FiCheckCircle,
    FiEye,
    FiEyeOff,
    FiLock,
    FiShield,
} from "react-icons/fi";

import EmployeePermissionMatrix
    from "./EmployeePermissionMatrix";

import {
    formatEmployeeLabel,
    getObjectId,
    getPermissionFeature,
} from "../utils/employee.utils";

import {
    PERMISSION_MODES,
} from "../utils/employee.constants";

import "./EmployeeCommon.css";


/*
|--------------------------------------------------------------------------
| EMPLOYEE ACCESS SECTION
|--------------------------------------------------------------------------
*/

const EmployeeAccessSection = ({
    form,

    setForm,

    roles = [],

    permissionModules = [],

    errors = {},

    setErrors,

    loading = false,

    existingLoginAccount = false,
}) => {

    /*
    |--------------------------------------------------------------------------
    | PASSWORD
    |--------------------------------------------------------------------------
    */

    const [
        showPassword,
        setShowPassword,
    ] =
        useState(
            false
        );


    /*
    |--------------------------------------------------------------------------
    | SELECTED ROLE
    |--------------------------------------------------------------------------
    */

    const selectedRole =
        useMemo(
            () => {

                return (
                    roles.find(
                        (
                            role
                        ) =>
                            getObjectId(
                                role
                            ) ===
                            String(
                                form?.roleId ||
                                ""
                            )
                    ) ||
                    null
                );

            },
            [
                roles,
                form?.roleId,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | ROLE EFFECTIVE PERMISSION IDS
    |--------------------------------------------------------------------------
    |
    | New backend provides:
    |
    | effectivePermissionIds
    |
    | These contain only permissions that are currently available according
    | to the tenant's active subscription.
    |
    | Fallback to permissions for compatibility with older API response.
    |
    |--------------------------------------------------------------------------
    */

    const rolePermissionIds =
        useMemo(
            () => {

                if (
                    !selectedRole
                ) {

                    return [];

                }


                const values =
                    Array.isArray(
                        selectedRole
                            ?.effectivePermissionIds
                    )
                        ?
                        selectedRole
                            .effectivePermissionIds
                        :
                        Array.isArray(
                            selectedRole
                                ?.permissions
                        )
                            ?
                            selectedRole
                                .permissions
                            :
                            [];


                return [
                    ...new Set(
                        values
                            .map(
                                (
                                    permission
                                ) =>
                                    getObjectId(
                                        permission
                                    )
                            )
                            .filter(
                                Boolean
                            )
                    ),
                ];

            },
            [
                selectedRole,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | ROLE PERMISSION SET
    |--------------------------------------------------------------------------
    */

    const rolePermissionSet =
        useMemo(
            () => {

                return new Set(
                    rolePermissionIds
                );

            },
            [
                rolePermissionIds,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | ROLE PERMISSION COUNT
    |--------------------------------------------------------------------------
    */

    const rolePermissionCount =
        rolePermissionIds
            .length;


    /*
    |--------------------------------------------------------------------------
    | ORIGINAL ROLE PERMISSION COUNT
    |--------------------------------------------------------------------------
    */

    const originalRolePermissionCount =
        useMemo(
            () => {

                if (
                    selectedRole
                        ?.totalPermissionCount !==
                    undefined
                ) {

                    return Number(
                        selectedRole
                            .totalPermissionCount ||
                        0
                    );

                }


                return Array.isArray(
                    selectedRole
                        ?.permissions
                )
                    ?
                    selectedRole
                        .permissions
                        .length
                    :
                    0;

            },
            [
                selectedRole,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | ROLE MODULE MATRIX
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    |
    | This is fully generated from backend permissionModules.
    |
    | Nothing like:
    |
    | EMPLOYEE
    | BILLING
    | INVENTORY
    |
    | is hardcoded here.
    |
    |--------------------------------------------------------------------------
    */

    const roleModuleMatrix =
        useMemo(
            () => {

                if (
                    !Array.isArray(
                        permissionModules
                    )
                ) {

                    return [];

                }


                return permissionModules
                    .map(
                        (
                            module
                        ) => {

                            const permissions =
                                Array.isArray(
                                    module
                                        ?.permissions
                                )
                                    ?
                                    module
                                        .permissions
                                    :
                                    [];


                            /*
                            |--------------------------------------------------------------------------
                            | PERMISSIONS INCLUDED IN SELECTED ROLE
                            |--------------------------------------------------------------------------
                            */

                            const selectedPermissions =
                                permissions
                                    .filter(
                                        (
                                            permission
                                        ) => {

                                            const permissionId =
                                                getObjectId(
                                                    permission
                                                );


                                            return (
                                                permissionId &&
                                                rolePermissionSet
                                                    .has(
                                                        permissionId
                                                    )
                                            );

                                        }
                                    );


                            /*
                            |--------------------------------------------------------------------------
                            | FEATURES
                            |--------------------------------------------------------------------------
                            |
                            | Example:
                            |
                            | EMPLOYEE
                            |
                            | Employee
                            | Attendance
                            | Leave
                            | Salary
                            | Advance
                            |
                            |--------------------------------------------------------------------------
                            */

                            const featureMap =
                                new Map();


                            permissions
                                .forEach(
                                    (
                                        permission
                                    ) => {

                                        const featureName =
                                            getPermissionFeature(
                                                permission
                                            );


                                        if (
                                            !featureMap.has(
                                                featureName
                                            )
                                        ) {

                                            featureMap.set(
                                                featureName,
                                                {
                                                    name:
                                                        featureName,

                                                    permissions:
                                                        [],
                                                }
                                            );

                                        }


                                        featureMap
                                            .get(
                                                featureName
                                            )
                                            .permissions
                                            .push(
                                                permission
                                            );

                                    }
                                );


                            const features =
                                Array.from(
                                    featureMap
                                        .values()
                                )
                                    .map(
                                        (
                                            feature
                                        ) => {

                                            const featurePermissions =
                                                feature
                                                    .permissions;


                                            const selectedFeaturePermissions =
                                                featurePermissions
                                                    .filter(
                                                        (
                                                            permission
                                                        ) => {

                                                            const permissionId =
                                                                getObjectId(
                                                                    permission
                                                                );


                                                            return (
                                                                permissionId &&
                                                                rolePermissionSet
                                                                    .has(
                                                                        permissionId
                                                                    )
                                                            );

                                                        }
                                                    );


                                            return {

                                                ...feature,

                                                selectedPermissions:
                                                    selectedFeaturePermissions,

                                                selectedCount:
                                                    selectedFeaturePermissions
                                                        .length,

                                                totalCount:
                                                    featurePermissions
                                                        .length,

                                                hasAccess:
                                                    selectedFeaturePermissions
                                                        .length >
                                                    0,

                                                hasFullAccess:
                                                    featurePermissions
                                                        .length >
                                                    0 &&
                                                    selectedFeaturePermissions
                                                        .length ===
                                                    featurePermissions
                                                        .length,

                                            };

                                        }
                                    );


                            return {

                                code:
                                    module?.code ||
                                    "",

                                name:
                                    module?.name ||
                                    formatEmployeeLabel(
                                        module?.code
                                    ),

                                description:
                                    module?.description ||
                                    "",

                                permissions,

                                selectedPermissions,

                                selectedCount:
                                    selectedPermissions
                                        .length,

                                totalCount:
                                    permissions
                                        .length,

                                hasAccess:
                                    selectedPermissions
                                        .length >
                                    0,

                                hasFullAccess:
                                    permissions
                                        .length >
                                    0 &&
                                    selectedPermissions
                                        .length ===
                                    permissions
                                        .length,

                                features,

                            };

                        }
                    )
                    /*
                    |--------------------------------------------------------------------------
                    | SHOW ONLY MODULES AVAILABLE IN CURRENT SUBSCRIPTION
                    |--------------------------------------------------------------------------
                    */
                    .filter(
                        (
                            module
                        ) =>
                            module
                                .totalCount >
                            0
                    );

            },
            [
                permissionModules,
                rolePermissionSet,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | ROLE MODULE ACCESS COUNT
    |--------------------------------------------------------------------------
    */

    const roleModuleAccessCount =
        useMemo(
            () => {

                return roleModuleMatrix
                    .filter(
                        (
                            module
                        ) =>
                            module
                                .hasAccess
                    )
                    .length;

            },
            [
                roleModuleMatrix,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | UPDATE FORM
    |--------------------------------------------------------------------------
    */

    const updateForm = (
        field,
        value
    ) => {

        setForm(
            (
                previous
            ) => ({
                ...previous,

                [field]:
                    value,
            })
        );


        setErrors?.(
            (
                previous
            ) => ({
                ...previous,

                [field]:
                    "",

                permissions:
                    "",

                form:
                    "",
            })
        );

    };


    /*
    |--------------------------------------------------------------------------
    | SWITCH PERMISSION MODE
    |--------------------------------------------------------------------------
    */

    const handlePermissionMode =
        (
            permissionMode
        ) => {

            updateForm(
                "permissionMode",
                permissionMode
            );


            /*
            |--------------------------------------------------------------------------
            | ROLE MODE DOES NOT STORE CUSTOM IDS
            |--------------------------------------------------------------------------
            */

            if (
                permissionMode ===
                PERMISSION_MODES.ROLE
            ) {

                updateForm(
                    "permissionIds",
                    []
                );

            }

        };


    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (

        <section className="employee-access-card">

            {/* ================================================================
                HEADING
            ================================================================ */}

            <div className="employee-access-heading">

                <span>
                    <FiLock />
                </span>


                <div>

                    <h3>
                        Login Access & Permissions
                    </h3>


                    <p>
                        Configure employee admin-panel access.
                    </p>

                </div>

            </div>


            {/* ================================================================
                LOGIN ACCESS
            ================================================================ */}

            <label className="employee-access-toggle">

                <input
                    type="checkbox"

                    checked={
                        Boolean(
                            form?.loginAccess
                        )
                    }

                    onChange={
                        (
                            event
                        ) =>
                            updateForm(
                                "loginAccess",
                                event
                                    .target
                                    .checked
                            )
                    }

                    disabled={
                        loading
                    }
                />


                <span />


                <div>

                    <strong>
                        Enable Employee Login
                    </strong>


                    <small>
                        Allow this employee to sign in to the business admin panel.
                    </small>

                </div>

            </label>


            {/* ================================================================
                ACCESS CONFIGURATION
            ================================================================ */}

            {
                form?.loginAccess
                    ?
                    (
                        <div className="employee-access-content">

                            {/* ====================================================
                                ROLE + PASSWORD
                            ==================================================== */}

                            <div className="employee-access-grid">

                                {/* =================================================
                                    ROLE
                                ================================================= */}

                                <div className="employee-access-field">

                                    <label>
                                        Tenant Role *
                                    </label>


                                    <select
                                        value={
                                            form?.roleId ||
                                            ""
                                        }

                                        onChange={
                                            (
                                                event
                                            ) =>
                                                updateForm(
                                                    "roleId",
                                                    event
                                                        .target
                                                        .value
                                                )
                                        }

                                        disabled={
                                            loading
                                        }
                                    >

                                        <option value="">
                                            Select role
                                        </option>


                                        {
                                            roles.map(
                                                (
                                                    role
                                                ) => (

                                                    <option
                                                        key={
                                                            getObjectId(
                                                                role
                                                            )
                                                        }

                                                        value={
                                                            getObjectId(
                                                                role
                                                            )
                                                        }
                                                    >

                                                        {
                                                            role?.name ||
                                                            role?.code
                                                        }

                                                    </option>

                                                )
                                            )
                                        }

                                    </select>


                                    {
                                        roles.length ===
                                        0
                                            ?
                                            (
                                                <small className="employee-access-field-help">
                                                    No employee-assignable tenant roles are available.
                                                </small>
                                            )
                                            :
                                            null
                                    }


                                    {
                                        errors?.roleId
                                            ?
                                            (
                                                <small className="employee-common-error">
                                                    {
                                                        errors.roleId
                                                    }
                                                </small>
                                            )
                                            :
                                            null
                                    }

                                </div>


                                {/* =================================================
                                    PASSWORD
                                ================================================= */}

                                <div className="employee-access-field">

                                    <label>

                                        {
                                            existingLoginAccount
                                                ?
                                                "New Password"
                                                :
                                                "Password *"
                                        }

                                    </label>


                                    <div className="employee-access-password">

                                        <input
                                            type={
                                                showPassword
                                                    ?
                                                    "text"
                                                    :
                                                    "password"
                                            }

                                            value={
                                                form?.password ||
                                                ""
                                            }

                                            onChange={
                                                (
                                                    event
                                                ) =>
                                                    updateForm(
                                                        "password",
                                                        event
                                                            .target
                                                            .value
                                                    )
                                            }

                                            placeholder={
                                                existingLoginAccount
                                                    ?
                                                    "Leave blank to keep existing password"
                                                    :
                                                    "Minimum 8 characters"
                                            }

                                            disabled={
                                                loading
                                            }
                                        />


                                        <button
                                            type="button"

                                            onClick={
                                                () =>
                                                    setShowPassword(
                                                        (
                                                            previous
                                                        ) =>
                                                            !previous
                                                    )
                                            }

                                            disabled={
                                                loading
                                            }
                                        >

                                            {
                                                showPassword
                                                    ?
                                                    <FiEyeOff />
                                                    :
                                                    <FiEye />
                                            }

                                        </button>

                                    </div>


                                    {
                                        errors?.password
                                            ?
                                            (
                                                <small className="employee-common-error">
                                                    {
                                                        errors.password
                                                    }
                                                </small>
                                            )
                                            :
                                            null
                                    }

                                </div>

                            </div>


                            {/* ====================================================
                                PERMISSION MODE TITLE
                            ==================================================== */}

                            <div className="employee-access-mode-title">

                                <FiShield />


                                <div>

                                    <strong>
                                        Permission Mode
                                    </strong>


                                    <span>
                                        Use role access or customize employee access.
                                    </span>

                                </div>

                            </div>


                            {/* ====================================================
                                PERMISSION MODE
                            ==================================================== */}

                            <div className="employee-access-mode-grid">

                                {/* =================================================
                                    ROLE
                                ================================================= */}

                                <button
                                    type="button"

                                    className={
                                        form?.permissionMode ===
                                        PERMISSION_MODES.ROLE
                                            ?
                                            "employee-access-mode active"
                                            :
                                            "employee-access-mode"
                                    }

                                    onClick={
                                        () =>
                                            handlePermissionMode(
                                                PERMISSION_MODES.ROLE
                                            )
                                    }

                                    disabled={
                                        loading
                                    }
                                >

                                    <span>

                                        {
                                            form?.permissionMode ===
                                            PERMISSION_MODES.ROLE
                                                ?
                                                <FiCheck />
                                                :
                                                null
                                        }

                                    </span>


                                    <div>

                                        <strong>
                                            Role Permissions
                                        </strong>


                                        <small>
                                            Automatically inherit access from the selected role.
                                        </small>

                                    </div>

                                </button>


                                {/* =================================================
                                    CUSTOM
                                ================================================= */}

                                <button
                                    type="button"

                                    className={
                                        form?.permissionMode ===
                                        PERMISSION_MODES.CUSTOM
                                            ?
                                            "employee-access-mode active"
                                            :
                                            "employee-access-mode"
                                    }

                                    onClick={
                                        () =>
                                            handlePermissionMode(
                                                PERMISSION_MODES.CUSTOM
                                            )
                                    }

                                    disabled={
                                        loading
                                    }
                                >

                                    <span>

                                        {
                                            form?.permissionMode ===
                                            PERMISSION_MODES.CUSTOM
                                                ?
                                                <FiCheck />
                                                :
                                                null
                                        }

                                    </span>


                                    <div>

                                        <strong>
                                            Custom Permissions
                                        </strong>


                                        <small>
                                            Configure exact module and action access.
                                        </small>

                                    </div>

                                </button>

                            </div>


                            {/* ====================================================
                                ROLE PERMISSIONS
                            ==================================================== */}

                            {
                                form?.permissionMode ===
                                PERMISSION_MODES.ROLE
                                    ?
                                    (
                                        <div className="employee-role-permission-section">

                                            {/* =====================================
                                                NO ROLE
                                            ===================================== */}

                                            {
                                                !selectedRole
                                                    ?
                                                    (
                                                        <div className="employee-role-summary">

                                                            <FiShield />


                                                            <div>

                                                                <strong>
                                                                    Select a role
                                                                </strong>


                                                                <span>
                                                                    Select a tenant role to preview inherited permissions.
                                                                </span>

                                                            </div>

                                                        </div>
                                                    )
                                                    :
                                                    (
                                                        <>

                                                            {/* =================================
                                                                ROLE SUMMARY
                                                            ================================= */}

                                                            <div className="employee-role-summary employee-role-summary-expanded">

                                                                <FiShield />


                                                                <div>

                                                                    <strong>
                                                                        {
                                                                            selectedRole
                                                                                ?.name ||
                                                                            selectedRole
                                                                                ?.code
                                                                        }
                                                                    </strong>


                                                                    <span>

                                                                        {
                                                                            selectedRole
                                                                                ?.description ||
                                                                            "Permissions are inherited automatically from this role."
                                                                        }

                                                                    </span>


                                                                    <div className="employee-role-summary-stats">

                                                                        <span>

                                                                            <strong>
                                                                                {
                                                                                    roleModuleAccessCount
                                                                                }
                                                                            </strong>

                                                                            {" "}
                                                                            module(s)

                                                                        </span>


                                                                        <span>

                                                                            <strong>
                                                                                {
                                                                                    rolePermissionCount
                                                                                }
                                                                            </strong>

                                                                            {" "}
                                                                            available permission(s)

                                                                        </span>


                                                                        {
                                                                            originalRolePermissionCount >
                                                                            rolePermissionCount
                                                                                ?
                                                                                (
                                                                                    <span className="employee-role-limited">

                                                                                        {
                                                                                            originalRolePermissionCount -
                                                                                            rolePermissionCount
                                                                                        }

                                                                                        {" "}
                                                                                        unavailable by current subscription

                                                                                    </span>
                                                                                )
                                                                                :
                                                                                null
                                                                        }

                                                                    </div>

                                                                </div>

                                                            </div>


                                                            {/* =================================
                                                                INFORMATION
                                                            ================================= */}

                                                            <div className="employee-role-inherited-note">

                                                                <FiCheckCircle />


                                                                <span>
                                                                    Permissions below are inherited from the selected role and are read-only here. Choose Custom Permissions to configure access individually.
                                                                </span>

                                                            </div>


                                                            {/* =================================
                                                                MODULE MATRIX
                                                            ================================= */}

                                                            <div className="employee-role-module-matrix">

                                                                {
                                                                    roleModuleMatrix
                                                                        .map(
                                                                            (
                                                                                module
                                                                            ) => (

                                                                                <div
                                                                                    key={
                                                                                        module
                                                                                            .code
                                                                                    }

                                                                                    className={
                                                                                        module
                                                                                            .hasAccess
                                                                                            ?
                                                                                            "employee-role-module active"
                                                                                            :
                                                                                            "employee-role-module"
                                                                                    }
                                                                                >

                                                                                    {/* =================
                                                                                        MODULE HEADER
                                                                                    ================= */}

                                                                                    <div className="employee-role-module-header">

                                                                                        <label>

                                                                                            <input
                                                                                                type="checkbox"

                                                                                                checked={
                                                                                                    module
                                                                                                        .hasAccess
                                                                                                }

                                                                                                readOnly

                                                                                                disabled
                                                                                            />


                                                                                            <span className="employee-role-module-checkbox">

                                                                                                {
                                                                                                    module
                                                                                                        .hasAccess
                                                                                                        ?
                                                                                                        <FiCheck />
                                                                                                        :
                                                                                                        null
                                                                                                }

                                                                                            </span>


                                                                                            <div>

                                                                                                <strong>

                                                                                                    {
                                                                                                        module
                                                                                                            .name
                                                                                                    }

                                                                                                </strong>


                                                                                                <small>

                                                                                                    {
                                                                                                        module
                                                                                                            .selectedCount
                                                                                                    }

                                                                                                    {" / "}

                                                                                                    {
                                                                                                        module
                                                                                                            .totalCount
                                                                                                    }

                                                                                                    {" permissions"}

                                                                                                </small>

                                                                                            </div>

                                                                                        </label>


                                                                                        {
                                                                                            module
                                                                                                .hasFullAccess
                                                                                                ?
                                                                                                (
                                                                                                    <span className="employee-role-access-badge full">
                                                                                                        Full Access
                                                                                                    </span>
                                                                                                )
                                                                                                :
                                                                                                module
                                                                                                    .hasAccess
                                                                                                    ?
                                                                                                    (
                                                                                                        <span className="employee-role-access-badge partial">
                                                                                                            Partial
                                                                                                        </span>
                                                                                                    )
                                                                                                    :
                                                                                                    (
                                                                                                        <span className="employee-role-access-badge none">
                                                                                                            No Access
                                                                                                        </span>
                                                                                                    )
                                                                                        }

                                                                                    </div>


                                                                                    {/* =================
                                                                                        FEATURES
                                                                                    ================= */}

                                                                                    {
                                                                                        module
                                                                                            .hasAccess
                                                                                            ?
                                                                                            (
                                                                                                <div className="employee-role-feature-list">

                                                                                                    {
                                                                                                        module
                                                                                                            .features
                                                                                                            .filter(
                                                                                                                (
                                                                                                                    feature
                                                                                                                ) =>
                                                                                                                    feature
                                                                                                                        .hasAccess
                                                                                                            )
                                                                                                            .map(
                                                                                                                (
                                                                                                                    feature
                                                                                                                ) => (

                                                                                                                    <div
                                                                                                                        key={
                                                                                                                            `${module.code}-${feature.name}`
                                                                                                                        }

                                                                                                                        className="employee-role-feature"
                                                                                                                    >

                                                                                                                        {/* =========
                                                                                                                            FEATURE
                                                                                                                        ========= */}

                                                                                                                        <div className="employee-role-feature-header">

                                                                                                                            <label>

                                                                                                                                <input
                                                                                                                                    type="checkbox"

                                                                                                                                    checked={
                                                                                                                                        feature
                                                                                                                                            .hasAccess
                                                                                                                                    }

                                                                                                                                    readOnly

                                                                                                                                    disabled
                                                                                                                                />


                                                                                                                                <span className="employee-role-feature-checkbox">

                                                                                                                                    {
                                                                                                                                        feature
                                                                                                                                            .hasAccess
                                                                                                                                            ?
                                                                                                                                            <FiCheck />
                                                                                                                                            :
                                                                                                                                            null
                                                                                                                                    }

                                                                                                                                </span>


                                                                                                                                <div>

                                                                                                                                    <strong>
                                                                                                                                        {
                                                                                                                                            feature
                                                                                                                                                .name
                                                                                                                                        }
                                                                                                                                    </strong>


                                                                                                                                    <small>

                                                                                                                                        {
                                                                                                                                            feature
                                                                                                                                                .selectedCount
                                                                                                                                        }

                                                                                                                                        {" / "}

                                                                                                                                        {
                                                                                                                                            feature
                                                                                                                                                .totalCount
                                                                                                                                        }

                                                                                                                                        {" actions"}

                                                                                                                                    </small>

                                                                                                                                </div>

                                                                                                                            </label>

                                                                                                                        </div>


                                                                                                                        {/* =========
                                                                                                                            ACTIONS
                                                                                                                        ========= */}

                                                                                                                        <div className="employee-role-action-grid">

                                                                                                                            {
                                                                                                                                feature
                                                                                                                                    .permissions
                                                                                                                                    .map(
                                                                                                                                        (
                                                                                                                                            permission
                                                                                                                                        ) => {

                                                                                                                                            const permissionId =
                                                                                                                                                getObjectId(
                                                                                                                                                    permission
                                                                                                                                                );


                                                                                                                                            const checked =
                                                                                                                                                rolePermissionSet
                                                                                                                                                    .has(
                                                                                                                                                        permissionId
                                                                                                                                                    );


                                                                                                                                            return (

                                                                                                                                                <label
                                                                                                                                                    key={
                                                                                                                                                        permissionId ||
                                                                                                                                                        permission
                                                                                                                                                            ?.code
                                                                                                                                                    }

                                                                                                                                                    className={
                                                                                                                                                        checked
                                                                                                                                                            ?
                                                                                                                                                            "employee-role-action active"
                                                                                                                                                            :
                                                                                                                                                            "employee-role-action"
                                                                                                                                                    }
                                                                                                                                                >

                                                                                                                                                    <input
                                                                                                                                                        type="checkbox"

                                                                                                                                                        checked={
                                                                                                                                                            checked
                                                                                                                                                        }

                                                                                                                                                        readOnly

                                                                                                                                                        disabled
                                                                                                                                                    />


                                                                                                                                                    <span className="employee-role-action-checkbox">

                                                                                                                                                        {
                                                                                                                                                            checked
                                                                                                                                                                ?
                                                                                                                                                                <FiCheck />
                                                                                                                                                                :
                                                                                                                                                                null
                                                                                                                                                        }

                                                                                                                                                    </span>


                                                                                                                                                    <div>

                                                                                                                                                        <strong>

                                                                                                                                                            {
                                                                                                                                                                formatEmployeeLabel(
                                                                                                                                                                    permission
                                                                                                                                                                        ?.action
                                                                                                                                                                )
                                                                                                                                                            }

                                                                                                                                                        </strong>


                                                                                                                                                        <small>

                                                                                                                                                            {
                                                                                                                                                                permission
                                                                                                                                                                    ?.name ||
                                                                                                                                                                permission
                                                                                                                                                                    ?.code
                                                                                                                                                            }

                                                                                                                                                        </small>

                                                                                                                                                    </div>

                                                                                                                                                </label>

                                                                                                                                            );

                                                                                                                                        }
                                                                                                                                    )
                                                                                                                            }

                                                                                                                        </div>

                                                                                                                    </div>

                                                                                                                )
                                                                                                            )
                                                                                                    }

                                                                                                </div>
                                                                                            )
                                                                                            :
                                                                                            null
                                                                                    }

                                                                                </div>

                                                                            )
                                                                        )
                                                                }

                                                            </div>

                                                        </>
                                                    )
                                            }

                                        </div>
                                    )
                                    :
                                    (
                                        /*
                                        |--------------------------------------------------------------------------
                                        | CUSTOM PERMISSIONS
                                        |--------------------------------------------------------------------------
                                        */

                                        <EmployeePermissionMatrix

                                            permissionModules={
                                                permissionModules
                                            }

                                            value={
                                                form?.permissionIds ||
                                                []
                                            }

                                            onChange={
                                                (
                                                    permissionIds
                                                ) =>
                                                    updateForm(
                                                        "permissionIds",
                                                        permissionIds
                                                    )
                                            }

                                            disabled={
                                                loading
                                            }

                                            error={
                                                errors?.permissions ||
                                                ""
                                            }

                                        />
                                    )
                            }

                        </div>
                    )
                    :
                    null
            }

        </section>

    );

};


export default
    EmployeeAccessSection;